import { useState, type SyntheticEvent } from 'react';
import Search from '@mui/icons-material/Search';
import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import { ToReviewList } from './components/to-review-list';
import { ContentBox } from '@/components/shared/content-box';
import { PageContainer } from '@/components/shared/page-container';
import { PageHeader } from '@/components/shared/page-header';
import { useResourceApprovalsToReviewList } from '@/data/subsidy/resource-approvals/to-review';
import { useSetTitle } from '@/hooks/use-set-title';

const tabLabels = [
  { label: 'Todos', value: 'all' },
  { label: 'Pendentes', value: 'pending' },
  { label: 'Aprovados', value: 'approved' },
  { label: 'Recusados', value: 'rejected' },
  { label: 'Cancelados', value: 'canceled' },
];

export const SubsidyManagement = () => {
  useSetTitle('Gestão de reembolsos');
  const [page, setPage] = useState(1);
  const [inputValue, setInputValue] = useState('');
  const [filterIdentifier, setFilterIdentifier] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortDirection, setSortDirection] = useState<'desc' | 'asc'>('desc');

  const requestParams = {
    status: filterStatus,
    page,
    per_page: 10,
    identifier: filterIdentifier,
    order: sortDirection,
  };

  const { resourceApprovals, totalItems, isFetching } =
    useResourceApprovalsToReviewList(requestParams);

  const handleFilterIdentifier = () => {
    setFilterIdentifier(inputValue);
    setPage(1);
  };

  const handleFilterChange = (_: SyntheticEvent, newValue: string) => {
    if (typeof newValue === 'string') {
      setFilterIdentifier('');
      setFilterStatus(newValue);
      setPage(1);
    }
  };

  const handleSortChange = (sort: { sort: 'desc' | 'asc' }[]) => {
    if (sort.length > 0) {
      const newDirection = sort[0].sort || 'desc';
      setSortDirection(newDirection);
      setPage(1);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Gestão de reembolsos"
        description="Gerencie, aprove e acompanhe todos os pedidos de reembolso."
      />
      <ContentBox>
        <Stack
          direction={{ md: 'row' }}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
          mb={3}
          gap={2}
        >
          <Grid
            container
            spacing={1}
            alignItems="center"
            size={{ xs: 12, md: 4 }}
          >
            <TextField
              sx={{ flex: 1 }}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Pesquise por usuário"
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  handleFilterIdentifier();
                }
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        sx={{ pl: 3 }}
                        aria-label="search"
                        onClick={handleFilterIdentifier}
                      >
                        <Search fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button variant="contained" onClick={handleFilterIdentifier}>
              Pesquisar
            </Button>
          </Grid>
          <Tabs
            value={filterStatus}
            onChange={handleFilterChange}
            orientation="horizontal"
            variant="scrollable"
          >
            {tabLabels.map(({ label, value }) => (
              <Tab key={value} label={label} value={value} />
            ))}
          </Tabs>
        </Stack>
        <ToReviewList
          toReview={resourceApprovals}
          pageState={{
            isLoading: isFetching,
            data: resourceApprovals || [],
            total: totalItems,
            page: page,
            pageSize: 10,
          }}
          onPaginationModelChange={(newPage) => {
            setPage(newPage.page + 1);
          }}
          onSortModelChange={(newSortModel) => handleSortChange(newSortModel)}
        />
      </ContentBox>
    </PageContainer>
  );
};
